import { Form, Link } from "@inertiajs/react"
import { Check, Copy, ExternalLink, Settings2 } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { useClipboard } from "@/hooks/use-clipboard"
import { useOnline } from "@/hooks/use-online"
import type { CandidateContactPanel as PanelData } from "@/types/candidates"

/**
 * OKS-951 — Contact panel on the candidate detail page (UX Spec §2/§3).
 *
 * # 🔴 CTPO decision D2 — the app SENDS NOTHING
 *
 * This panel renders a merged pitch with a copy button. A human sends it from
 * their own mailbox or TikTok, then marks the candidate contacted. The action
 * is « Marquer comme contacté·e » and **never** « Envoyer », in any state or
 * tooltip.
 *
 * That is not squeamishness about scope. A "Sent" state the app cannot verify
 * is how the follow-up queue fills with lies, and every cadence rule in this
 * ticket is measured off `attempted_at`. So the disclaimer sits **above** the
 * pitch rather than under the copy button — a disclaimer discovered after the
 * action is copy nobody reads — and the confirm dialog restates it, because
 * confirming is the moment the claim becomes a record.
 *
 * # Rendering is entirely prop-driven
 *
 * The panel does not exist unless the server sends `contact` (null outside
 * `{approved, contacted}`, on a soft-removed candidate, or for a viewer who
 * may not log an attempt). There is no status logic here — the same
 * hide-when-null rule the action bar uses.
 *
 * # The pitch block is READ-ONLY, and that is load-bearing
 *
 * What gets copied must be the rendered template, not text the user edited in
 * the box — otherwise `template_id` on the attempt row describes something
 * other than what was copied. It is a `<pre>`-style block, never a textarea.
 *
 * # Crew form rules
 *
 * Inertia `<Form>` with plain `RadioGroup` / `Input` / `Label` and `name`
 * attributes; errors from the render-prop `errors` object. **No shadcn `Form`,
 * no react-hook-form, no zod.**
 */
export interface CandidateContactPanelProps {
  contact: PanelData
  handle: string
  /** Scrolls the panel into view and focuses it — the card's « Relancer »
   *  deep-link arrives with `?focus=contact`. */
  autoFocus?: boolean
}

export function CandidateContactPanel({
  contact,
  handle,
  autoFocus = false,
}: CandidateContactPanelProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const [, copy] = useClipboard()
  const [copied, setCopied] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  // Default to the first AVAILABLE channel rather than the first channel:
  // when `candidate.email` is nil the Email option is disabled, and selecting
  // it by default would make the primary action refuse on the server for a
  // choice the user never made (UX Spec §3 Edge — "default selection falls to
  // DM TikTok").
  const firstAvailable =
    contact.channels.find((c) => c.available)?.value ?? "tiktok_dm"
  const [channel, setChannel] = useState<string>(firstAvailable)

  useEffect(() => {
    if (!autoFocus) return
    panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    panelRef.current?.focus()
  }, [autoFocus])

  const hasPitch = contact.pitch.state === "ok" && contact.pitch.body !== null
  const canSubmit = hasPitch && contact.submit_url !== null

  async function handleCopy() {
    if (contact.pitch.body === null) return
    const ok = await copy(contact.pitch.body)
    if (ok) {
      toast.success(t("candidates.contact.toast.copied"), {
        id: "toast-contact-copied",
      })
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } else {
      // Deliberately does NOT flip the label — the user keeps the option to
      // retry, and a "Copié !" that didn't copy is the same class of lie as a
      // "Sent" the app can't verify.
      toast.error(t("candidates.contact.toast.copy_failed"))
    }
  }

  return (
    <Card
      data-testid="candidate-detail-contact-panel"
      ref={panelRef}
      tabIndex={-1}
    >
      <CardHeader>
        <CardTitle>{t("candidates.contact.title")}</CardTitle>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {/*
          🔴 D2 banner, ABOVE everything else (UX Spec §2.2). Muted
          `role="status"` text, not an `Alert`: this is how the feature works,
          not an error to fix. Copy is PER CHANNEL — "paste it into TikTok"
          means nothing to someone who picked Email.
        */}
        <p
          role="status"
          data-testid="candidate-detail-contact-disclaimer"
          className="text-muted-foreground text-sm"
        >
          {t(`candidates.contact.disclaimer.${channel}`)}
        </p>

        {/*
          RadioGroup, not Select (UX Spec §2.3): each option carries a
          consequence line, and a Select can only show the chosen label. The
          consequence is wired via `aria-describedby` so a screen reader hears
          it rather than just the word — the pattern `BrandSettingsCandidates`
          established, for the same reason.
        */}
        <fieldset className="grid gap-3">
          <legend className="mb-2 text-sm font-medium">
            {t("candidates.contact.channel.legend")}
          </legend>

          <RadioGroup
            name="contact_attempt[channel]"
            value={channel}
            onValueChange={setChannel}
            data-testid="candidate-detail-contact-channel"
          >
            {contact.channels.map((option) => {
              const describedBy = `contact_channel_${option.value}_description`
              return (
                <div key={option.value} className="flex items-start gap-3">
                  <RadioGroupItem
                    value={option.value}
                    id={`contact_channel_${option.value}`}
                    disabled={!option.available}
                    aria-describedby={describedBy}
                    data-testid={`candidate-detail-contact-channel-${option.value}`}
                    className="mt-1"
                  />
                  <div className="grid gap-1">
                    {/*
                      The explicit `-label` id is belt-and-braces, not a
                      requirement: Base UI resolves this radio's accessible name
                      from the native `<label htmlFor>` on its own (it renders a
                      hidden `<input>` carrying the `id`, then labels the span
                      from that input's associated `<label>`). Setting the id
                      here just pins the value Base UI would generate anyway.
                    */}
                    <Label
                      id={`contact_channel_${option.value}-label`}
                      htmlFor={`contact_channel_${option.value}`}
                    >
                      {t(`candidates.contact.channel.options.${option.value}`)}
                    </Label>
                    {/*
                      The disabled reason lives in the accessible name's
                      described-by, NOT a tooltip: tooltips are unreachable by
                      touch, and this is the one thing a user needs in order to
                      understand why they can't pick Email (UX Spec §3 Edge).
                    */}
                    {/*
                      ⚠️ Keyed on the CHANNEL as well as availability, not on
                      `!option.available` alone. Email is the only channel that
                      can be unavailable today, so the two are equivalent right
                      now — but if `tiktok_dm` ever gains an availability rule
                      it would carry an EMAIL-named testid, and a locator would
                      silently match the wrong row rather than failing. (LQE
                      filed this as sub-80 and did not ask for it; it is one
                      line and the failure mode is a silent mismatch.)
                    */}
                    <p
                      id={describedBy}
                      data-testid={
                        !option.available && option.value === "email"
                          ? "candidate-detail-contact-email-disabled"
                          : undefined
                      }
                      className="text-muted-foreground text-sm"
                    >
                      {option.unavailable_reason ??
                        t(
                          `candidates.contact.channel.consequences.${option.value}`,
                        )}
                    </p>
                  </div>
                </div>
              )
            })}
          </RadioGroup>
        </fieldset>

        {/* ---- pitch: three distinct surfaces, never one generic error ---- */}

        {contact.pitch.state === "missing_template" && (
          <div
            data-testid="candidate-detail-contact-no-template"
            className="border-border flex flex-col items-start gap-2 rounded-lg border border-dashed p-4"
          >
            <p className="text-muted-foreground text-sm">
              {t("candidates.contact.errors.no_template")}
            </p>
            {contact.settings_url && (
              <Button asChild variant="outline" size="sm">
                <Link href={contact.settings_url}>
                  <Settings2 className="size-4" aria-hidden="true" />
                  {t("candidates.contact.errors.no_template_cta")}
                </Link>
              </Button>
            )}
          </div>
        )}

        {contact.pitch.state === "unknown_field" && (
          <p
            role="alert"
            data-testid="candidate-detail-contact-template-error"
            className="text-destructive text-sm"
          >
            {t("candidates.contact.errors.unknown_field", {
              field: contact.pitch.field,
            })}
          </p>
        )}

        {hasPitch && (
          <>
            {/*
              READ-ONLY, never a textarea: what is copied must be the rendered
              template, not text the user edited — otherwise `template_id` on
              the attempt row describes something else. Scrolls past ~12 lines.
            */}
            <pre
              data-testid="candidate-detail-contact-pitch"
              className="bg-muted max-h-72 overflow-y-auto rounded-lg p-4 text-sm break-words whitespace-pre-wrap"
            >
              {contact.pitch.body}
            </pre>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                // `void` rather than passing the async fn directly: an
                // unhandled rejection here would be a silent no-op on the one
                // action the user is relying on. `handleCopy` already resolves
                // its own failure into a toast.
                onClick={() => void handleCopy()}
                data-testid="candidate-detail-contact-copy"
              >
                {copied ? (
                  <Check className="size-4" aria-hidden="true" />
                ) : (
                  <Copy className="size-4" aria-hidden="true" />
                )}
                {copied
                  ? t("candidates.contact.copy.copied")
                  : t("candidates.contact.copy.label")}
              </Button>

              {/* DM channel only — the deep link is meaningless on Email. */}
              {channel === "tiktok_dm" && (
                <Button asChild variant="ghost" size="sm">
                  <a
                    href={contact.tiktok_profile_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="candidate-detail-contact-tiktok-link"
                  >
                    <ExternalLink className="size-4" aria-hidden="true" />
                    {t("candidates.contact.tiktok_link")}
                  </a>
                </Button>
              )}
            </div>
          </>
        )}

        {/* ---- the form: note + primary action ---- */}

        {canSubmit && (
          <Form
            method="post"
            action={contact.submit_url!}
            options={{ preserveScroll: true }}
            data-testid="candidate-detail-contact-form"
          >
            {({ errors, processing, submit }) => (
              <div className="flex flex-col gap-3">
                {/* The channel travels with the form. */}
                <input
                  type="hidden"
                  name="contact_attempt[channel]"
                  value={channel}
                />

                <div className="grid gap-1.5">
                  <Label htmlFor="contact_attempt_note">
                    {t("candidates.contact.note.label")}
                  </Label>
                  <Input
                    id="contact_attempt_note"
                    name="contact_attempt[note]"
                    placeholder={t("candidates.contact.note.placeholder")}
                    disabled={processing}
                    data-testid="candidate-detail-contact-note"
                  />
                </div>

                {/*
                  Server-side refusals land here (UX Spec §3 "Submit rejected
                  by the server") — the panel keeps its state and the pitch
                  stays copyable.
                */}
                {errors.channel && (
                  <p role="alert" className="text-destructive text-sm">
                    {errors.channel}
                  </p>
                )}

                <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                  <Button
                    type="button"
                    // Offline: the submit disables but the copy button above
                    // stays enabled — the clipboard works offline and copying
                    // is the useful half (UX Spec §3 Edge).
                    disabled={processing || !online}
                    aria-disabled={processing || !online}
                    onClick={() => setConfirmOpen(true)}
                    data-testid="candidate-detail-contact-submit"
                  >
                    {processing
                      ? t("candidates.contact.submit.processing")
                      : t("candidates.contact.submit.label")}
                  </Button>

                  <AlertDialogContent data-testid="candidate-detail-contact-confirm">
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {t("candidates.contact.confirm.title", { handle })}
                      </AlertDialogTitle>
                      {/*
                        🔴 Restates D2 at the moment the claim becomes a record.
                        Every cadence rule is measured off this timestamp, so
                        confirming without having sent is what makes the queue
                        untrustworthy.
                      */}
                      <AlertDialogDescription>
                        {t("candidates.contact.confirm.body")}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>
                        {t("candidates.contact.confirm.cancel")}
                      </AlertDialogCancel>
                      <AlertDialogAction
                        // eslint-disable-next-line no-restricted-syntax -- closes on click by design (OKS-1023)
                        onClick={() => submit()}
                        data-testid="candidate-detail-contact-confirm-submit"
                      >
                        {t("candidates.contact.confirm.confirm")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
          </Form>
        )}

        {/* ---- attempt history ---- */}

        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-medium">
            {t("candidates.contact.history.title", {
              count: contact.attempts.length,
            })}
          </h3>

          {contact.attempts.length === 0 ? (
            <p
              data-testid="candidate-detail-contact-history-empty"
              className="text-muted-foreground text-sm"
            >
              {t("candidates.contact.history.empty")}
            </p>
          ) : (
            <ol className="flex flex-col gap-2">
              {/*
                Newest-first from the server. `n` counts DOWN from the total so
                "Tentative 3" is the third attempt made, not the first row
                rendered — the number has to mean the same thing as the count in
                the heading.
              */}
              {contact.attempts.map((attempt, index) => (
                <li
                  key={attempt.id}
                  data-testid="candidate-detail-contact-attempt-row"
                  className="border-border flex flex-col gap-0.5 border-l-2 pl-3 text-sm"
                >
                  <span>
                    {t("candidates.contact.history.row", {
                      n: contact.attempts.length - index,
                      channel: t(
                        `candidates.contact.channel.options.${attempt.channel}`,
                      ),
                      date: new Intl.DateTimeFormat(undefined, {
                        dateStyle: "medium",
                      }).format(new Date(attempt.attempted_at)),
                      actor: attempt.actor_name ?? "—",
                    })}
                  </span>
                  {attempt.note && (
                    <span className="text-muted-foreground">
                      {attempt.note}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          )}
        </section>
      </CardContent>
    </Card>
  )
}
